import { useRef } from "react";
import { motion } from "framer-motion";
import IntroScene from "./scenes/IntroScene";
import TransformationView, { DataInspector } from "./TransformationView";

import POSScene from "./scenes/POSScene";
import { useSimulatorStore } from "@store/simulatorStore";

export const EMV_TAGS_EXTRACTED = {
  "Tag 5A (PAN)": "4111111111111111",
  "Tag 5F24 (Expiry)": "281231",
  "Tag 5F20 (Name)": "AGARWAL/ARNAV",
  "Tag 57 (Track 2 Equivalent)": "4111111111111111D2812201000000000000",
  "Tag 9F26 (ARQC)": "8A2C9F81B24E7D6A",
  "Tag 9F36 (ATC)": "004A",
  "Tag 9F37 (Unpredictable Number)": "7E3A9D1C",
  "Tag 95 (TVR)": "0000000000"
};

function DataStructuredStep({ state }: any) {
  const setAmount = useSimulatorStore((s) => s.setAmount);
  const amount = state.amount || "120.00";

  const terminalConfig = {
    terminal_id: "POS-8821A",
    currency: "USD",
    txn_type: "PURCHASE",
    entry_mode: "05"
  };

  const inputGraphic = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <div style={{ color: "#94A3B8", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Terminal Config</div>
        <DataInspector data={terminalConfig} />
      </div>
      <div style={{ borderTop: "1px dashed rgba(255,255,255,0.1)", paddingTop: 12 }}>
        <div style={{ color: "#94A3B8", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>EMV Data</div>
        <DataInspector data={EMV_TAGS_EXTRACTED} />
      </div>
    </div>
  );

  const processGraphic = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <div style={{ color: "#38bdf8", fontSize: "clamp(12px, 1.5vw, 14px)", fontWeight: 600 }}>Payload Mapping</div>
      
      {/* Amount Input beautifully integrated into the process arrow */}
      <div style={{ 
        background: "rgba(15, 23, 42, 0.9)", 
        border: "1px solid #38bdf8", 
        borderRadius: 8, 
        padding: "4px 12px", 
        display: "flex", 
        alignItems: "center",
        boxShadow: "0 0 10px rgba(56,189,248,0.2)"
      }}>
        <span style={{ color: "#94A3B8", fontSize: 10, marginRight: 8, textTransform: "uppercase" }}>Amount</span>
        <span style={{ color: "#38bdf8", fontSize: 16, marginRight: 4, fontWeight: 700 }}>$</span>
        <input 
          type="number" 
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          style={{ background: "transparent", border: "none", color: "#FFF", fontSize: 16, fontWeight: 700, outline: "none", width: 60 }}
        />
      </div>
    </div>
  );

  const dynamicOutput = {
    transaction_type: terminalConfig.txn_type,
    amount: (parseFloat(amount) || 0).toFixed(2),
    currency_code: terminalConfig.currency,
    card_data: {
      pan: EMV_TAGS_EXTRACTED["Tag 5A (PAN)"],
      expiry: EMV_TAGS_EXTRACTED["Tag 5F24 (Expiry)"],
      cardholder_name: EMV_TAGS_EXTRACTED["Tag 5F20 (Name)"],
      track2_data: EMV_TAGS_EXTRACTED["Tag 57 (Track 2 Equivalent)"]
    },
    emv_data: {
      arqc: EMV_TAGS_EXTRACTED["Tag 9F26 (ARQC)"],
      atc: EMV_TAGS_EXTRACTED["Tag 9F36 (ATC)"],
      unpredictable_number: EMV_TAGS_EXTRACTED["Tag 9F37 (Unpredictable Number)"],
      tvr: EMV_TAGS_EXTRACTED["Tag 95 (TVR)"]
    },
    pos_entry_mode: terminalConfig.entry_mode,
    terminal_id: terminalConfig.terminal_id
  };

  return (
    <TransformationView
      title="Data Structured"
      input={inputGraphic}
      process={processGraphic}
      output={dynamicOutput}
      explanationTitle="Payload Normalization"
      explanation={`The raw EMV data is merged with the Terminal's Configuration (Currency, Terminal ID, Entry Mode) and the exact human Input (Amount). 

This creates the perfectly structured "Golden Record" payload containing everything the network needs before protocol encoding.`}
    />
  );
}

function CardCapturedStep() {
  const cardGraphic = (
    <div style={{
      width: "100%", height: 120, background: "rgba(245, 245, 250, 0.95)",
      borderRadius: 12, padding: 16, position: "relative",
      boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
      display: "flex", flexDirection: "column", justifyContent: "space-between"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ width: 32, height: 24, background: "#eab308", borderRadius: 4, boxShadow: "inset 0 0 5px rgba(0,0,0,0.2)" }} />
        <div style={{ fontSize: 16, fontWeight: 900, color: "#1A1F71", fontStyle: "italic" }}>VISA</div>
      </div>
      <div style={{ color: "#333", fontSize: 16, fontFamily: "monospace", letterSpacing: 2 }}>
        4111 1111 1111 1111
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", color: "#555", fontSize: 10, fontFamily: "monospace" }}>
        <span>ARNAV AGARWAL</span>
        <span>12/28</span>
      </div>
    </div>
  );

  return (
    <TransformationView
      title="Card Captured"
      input={cardGraphic}
      process="APDU Handshake & Luhn Check"
      output={EMV_TAGS_EXTRACTED}
      explanationTitle="EMV Contactless Protocol"
      explanation={`When the card enters the terminal's RF field, it powers up via electromagnetic induction. The terminal and card begin the Application Selection phase.

1. SELECT PPSE: The terminal sends a "SELECT Proximity Payment System Environment" APDU command to discover available payment applications (e.g., Visa Credit).
2. GPO (Get Processing Options): The terminal requests the card's processing options, which returns the critical risk data (AIP) and file locators (AFL).
3. READ RECORD: The terminal executes READ RECORD commands to extract the BER-TLV encoded tags from the card's memory. This includes the PAN, Expiry Date, Track 2 Equivalent Data, and the Application Cryptogram.

Finally, the terminal runs a Modulo 10 (Luhn) check on the PAN to verify mathematical integrity before continuing.`}
    />
  );
}

function ValueExchangeStep() {
  const iframeRef = useRef<HTMLIFrameElement>(null);


  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "#000",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      zIndex: 5
    }}>
      <iframe
        ref={iframeRef}
        src="/assets/value_exchange.html"
        style={{
          width: "100%",
          height: "100%",
          border: "none"
        }}

      />

    </div>
  );
}

export default function StepScene({ step, state, onStart }: any) {
  switch (step) {
    case "INIT":
      return <IntroScene onStart={onStart} />;

    case "POS_ENTRY":
      return <POSScene onComplete={() => onStart()} />;

    case "CARD_CAPTURED":
      return <CardCapturedStep />;

    case "DATA_STRUCTURED":
      return <DataStructuredStep state={state} />;

    case "ISO_BUILT":
      return (
        <TransformationView
          title="ISO Message Built"
          input={state.structuredData}
          process="ISO 8583 Encoding"
          output={state.isoMessage}
          explanationTitle="ISO-8583 Message Construction"
          explanation={`The structured JSON payload is completely useless to legacy banking mainframes. It must be forged into an ISO-8583 message—the undisputed global language of money.

1. Bitmap Generation: A 64-bit binary sequence is created where every '1' signals the presence of a specific field (e.g., bit 2 for PAN, bit 4 for Amount).
2. Field 4 (Amount): Notice how the amount you entered was converted! In ISO 8583, the amount is ALWAYS 12 digits, zero-padded, representing the minor currency unit (cents). So $150.00 becomes exactly "000000015000". This is not a junk amount; it is the strict mathematical standard.
3. Data Serialization: The data is stripped of all JSON formatting and concatenated into a massive, contiguous string based on the bitmap's exact instructions.`}
        />
      );

    case "ENCRYPTED":
      return (
        <TransformationView
          title="Encryption"
          input={state.isoMessage}
          process="Pseudo AES Encryption"
          output={state.encryptedPayload}
          explanationTitle="Cryptographic Sealing"
          explanation={`Transmitting plain text over the internet is financial suicide. Before a single byte leaves the terminal, the payload is cryptographically sealed.

In a live environment, the terminal's hardware security module (HSM) uses complex symmetric encryption (like AES-256 or Triple DES) and DUKPT (Derived Unique Key Per Transaction) to ensure the encryption key changes for every single swipe. 

Here, we visualize this through an XOR transformation—a mathematical operation that scrambles the data into absolute gibberish. Even if a bad actor intercepts the traffic mid-flight, they will see nothing but useless noise. Only the Acquirer possesses the private keys required to shatter this vault.`}
        />
      );

    case "ROUTED":
      return (
        <TransformationView
          title="Network Routing"
          input={state.encryptedPayload}
          process="Terminal → Acquirer → Network → Issuer"
          output={state.routingTrace}
          explanationTitle="The 4-Party Model & Network Switching"
          explanation={`The encrypted payload is now fired into the global financial nervous system. The message is NOT simply passed along; it is actively mutated by each node.
 
1. The Acquirer: The merchant's bank receives the raw transmission. They act as the initial gateway and append an Acquirer Reference Number (ARN) and Forwarding Institution ID to the ISO payload.
2. The Switch: The transaction hits a Payment Network (Visa, Mastercard, Amex). The network acts as a hyper-fast router. It reads the BIN (Bank Identification Number) from the PAN to determine exactly where in the world the customer's bank is located. Critically, the Switch appends Network Reference Numbers and runs lightning-fast risk models, injecting a Fraud Risk Score into the message.
3. The Issuer: The packet travels thousands of miles in milliseconds, arriving at the customer's bank for final authorization.
 
At this exact moment, the payload has successfully navigated the labyrinth of global finance, carrying the forensic footprints of every server it touched.`}
        />
      );

    case "AUTHORIZED":
      return (
        <TransformationView
          title="Issuer Authorization (0110 Response)"
          input={{ 
            received_payload: state.encryptedPayload, 
            network_headers: state.routingTrace 
          }}
          process={
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
              <div style={{ color: "#38BDF8", fontWeight: "bold", fontSize: "14px" }}>Decrypt & Evaluate</div>
              <div style={{ fontSize: 11, fontFamily: "monospace", color: "#38BDF8", width: "100%", textAlign: "left", border: "1px solid rgba(56,189,248,0.2)", borderRadius: 6, padding: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ color: "#94A3B8" }}>Extract Amount:</span>
                  <span>${state.structuredData?.amount}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ color: "#94A3B8" }}>Extract Fraud Score:</span>
                  <span style={{ color: parseInt(state.routingTrace?.hop_2?.trace_data?.fraud_risk_score) > 85 ? "#EF4444" : "#10B981" }}>
                    {state.routingTrace?.hop_2?.trace_data?.fraud_risk_score}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#94A3B8" }}>Verify ARQC:</span>
                  <span style={{ color: "#10B981" }}>VALID</span>
                </div>
              </div>
            </div>
          }
          output={
            <div style={{ position: "relative" }}>
              {state.decision?.status === "APPROVED" && (
                <div style={{ 
                  position: "absolute", 
                  top: -70, 
                  left: 0, 
                  right: 0, 
                  display: "flex", 
                  justifyContent: "center",
                  zIndex: 30 
                }}>
                  <motion.div 
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    style={{
                      background: "rgba(34, 197, 94, 0.2)",
                      border: "1px solid #22C55E",
                      color: "#22C55E",
                      padding: "8px 24px",
                      borderRadius: 40,
                      fontSize: 14,
                      fontWeight: 900,
                      letterSpacing: 4,
                      boxShadow: "0 0 30px rgba(34, 197, 94, 0.5)",
                      backdropFilter: "blur(10px)",
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      whiteSpace: "nowrap"
                    }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22C55E", boxShadow: "0 0 15px #22C55E" }} />
                    APPROVED
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22C55E", boxShadow: "0 0 15px #22C55E" }} />
                  </motion.div>
                </div>
              )}
              <DataInspector data={state.authResponsePayload} />
            </div>
          }
          explanationTitle="Authorization Engine"
          explanation={`The final boss: The Issuing Bank's Decision Engine.
 
Once verified, the Issuer generates an Authorization Response Code (e.g., "00" for APPROVED, "51" for INSUFFICIENT FUNDS). This response code is fired back through the exact same network path, taking mere milliseconds to reach the POS terminal, finally turning the screen green and completing the transaction lifecycle.`}
        />
      );

    case "COMPLETED":
      return <ValueExchangeStep />;

    default:
      return <div>Initializing...</div>;
  }
}